/**
 * Snag Fit Key — all-in-one production script.
 * Regions · Sizing engine · Wizard UI · Storage · PDP integration.
 * German UI for .de domain. All other regions use English.
 */
(function (global) {
  'use strict';
  if (global.SnagFitKey) return;

  // ─── KILL SWITCH ────────────────────────────────────────────────────────────
  // TEMPORARY: set to true to hide the ENTIRE Fit Key everywhere (PDP "Find your
  // size" trigger, size-guide banner, and the overlay) and disable all behaviour
  // — nothing renders, nothing auto-opens. Flip back to false to re-enable. One line.
  var FIT_KEY_DISABLED = false;

  // ─── REPORTING ──────────────────────────────────────────────────────────────
  // Single source string for signup attribution. Used in three places that must agree:
  //   1. custom_source on the client/subscriptions call → lands in the consent
  //      record's custom_method_detail. This is the AUTHORITATIVE signup number,
  //      because it is written by the subscribe itself: it cannot be lost even if the
  //      events below never fire (see the 2.5s window in submitEmailSignup).
  //   2. the `Snag Fit Key Signup Source` profile property.
  //   3. the `Signup Source` property on the Fit Key Email Signup event.
  var FK_SIGNUP_SOURCE = 'Snag Fit Key';

  // ─── REGIONS ──────────────────────────────────────────────────────────────

  var UK_BANDS  = [['4/6','4/6'],['8/10','8/10'],['12/14','12/14'],['16/18','16/18'],['20/22','20/22'],['24/26','24/26'],['28-36','28-36']];
  var US_BANDS  = [['0-2','4/6'],['4-6','8/10'],['8-10','12/14'],['12-14','16/18'],['16-18','20/22'],['20-22','24/26'],['24-32','28-36']];
  var EU_BANDS  = [['32/34','4/6'],['36/38','8/10'],['40/42','12/14'],['44/46','16/18'],['48/50','20/22'],['52/54','24/26'],['56/64','28-36']];
  var RETAIL_UK = [4,6,8,10,12,14,16,18,20,22,24,26,28,30,32,34,36,38];

  var REGIONS = {
    UK: { label:'UK', defaultUnit:'cm', regionShift:0,  sizeSystem:'UK', tightsBands:UK_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ cm:['161 cm or shorter','161–174 cm','175 cm or taller'], in:['5\'3" or shorter','Over 5\'3" to 5\'8"','5\'9" or taller'] } },
    US: { label:'US', defaultUnit:'in', regionShift:-4, sizeSystem:'US', tightsBands:US_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ in:['5\'3" or shorter','Over 5\'3" to 5\'8"','Over 5\'8" to 6\'5"'], cm:['161 cm or shorter','161–174 cm','175 cm or taller'] } },
    CA: { label:'CA', defaultUnit:'in', regionShift:-4, sizeSystem:'US', tightsBands:US_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ in:['5\'3" or shorter','Over 5\'3" to 5\'8"','Over 5\'8" to 6\'5"'], cm:['161 cm or shorter','161–174 cm','175 cm or taller'] } },
    EU: { label:'EU', defaultUnit:'cm', regionShift:28, sizeSystem:'EU', tightsBands:EU_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ cm:['161 cm or shorter','161–174 cm','175 cm or taller'], in:['5\'3" or shorter','Over 5\'3" to 5\'8"','Over 5\'8"'] } },
    DE: { label:'DE', defaultUnit:'cm', regionShift:28, sizeSystem:'EU', tightsBands:EU_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ cm:['Bis 161 cm','162–174 cm','175 cm oder größer'], in:['5\'3" oder kleiner','Über 5\'3" bis 5\'8"','Über 5\'8"'] } },
    AU: { label:'AU', defaultUnit:'cm', regionShift:0,  sizeSystem:'UK', tightsBands:UK_BANDS, retailSizes:RETAIL_UK,
          heightLabels:{ cm:['161 cm or shorter','161–174 cm','175 cm or taller'], in:['5\'3" or shorter','Over 5\'3" to 5\'8"','Over 5\'8" to 6\'5"'] } },
  };

  var REGION_TOGGLE = ['UK','EU','DE','US','CA','AU'];

  function regionFromHost(host) {
    var h = (host || '').toLowerCase();
    if (h.indexOf('.com.au') !== -1) return 'AU';
    if (h.indexOf('.us')     !== -1) return 'US';
    if (h.indexOf('.ca')     !== -1) return 'CA';
    if (h.indexOf('.de')     !== -1) return 'DE';
    if (h.indexOf('.eu')     !== -1) return 'EU';
    return 'UK';
  }

  // Brand domain shown on the size card, keyed off the detected/selected region —
  // so it always reads as the clean storefront domain (never the .myshopify.com host).
  var BRAND_DOMAINS = { UK:'snagtights.com', US:'snagtights.us', CA:'snagtights.ca', EU:'snagtights.eu', DE:'omecsshop.com', AU:'snagtights.com.au' };
  function brandDomain() { return BRAND_DOMAINS[state.region] || 'snagtights.com'; }
  function supportEmail() { return 'hello@' + brandDomain(); }

  var INPUT_SNAPSHOT_KEYS = ['path','unit','region','build','clothingMeasOpen','topSize','bottomSize','heightBand','heightCm','bustCm','waistCm','hipsCm','braMode','braBand','braCup','underbustCm','bustFullCm'];

  function snapshotInputs() {
    var s = {};
    for (var i = 0; i < INPUT_SNAPSHOT_KEYS.length; i++) {
      var k = INPUT_SNAPSHOT_KEYS[i];
      s[k] = state[k];
    }
    return s;
  }

  function syncBustFullFromBust() {
    if (state.braMode === 'figure' && !state.bustFullCm && state.bustCm) state.bustFullCm = state.bustCm;
  }

  // Tights chart fork (curvy vs straighter). The shape question is asked on BOTH
  // the usual-size and measure paths, so build is always user-set by the time we
  // compute; default to curvy only as a defensive fallback if it is somehow unset.
  function tightsBuild() {
    return state.build === 'athletic' ? 'athletic' : 'curvy';
  }

  // ─── SIZING TABLES ────────────────────────────────────────────────────────

  var TIGHTS = {
    curvy:   { '4/6':['A/B','A/B','C'], '8/10':['C','C','D'], '12/14':['D','D','E'], '16/18':['E','E','F'], '20/22':['Short F','F','G'], '24/26':['Short F','F','G'], '28-36':['G','G','G'] },
    athletic:{ '4/6':['A/B','A/B','A/B'], '8/10':['A/B','A/B','C'], '12/14':['C','C','D'], '16/18':['D','D','E'], '20/22':['E','E','F'], '24/26':['Short F','F','G'], '28-36':['G','G','G'] },
  };

  var CLOTHES = {
    order: ['A/B','C','D','E','F','G','H','I','J'],
    chest: [[82,'A/B'],[92,'C'],[102,'D'],[113,'E'],[126,'F'],[138,'G'],[150,'H'],[162,'I'],[171,'J']],
    waist: [[64,'A/B'],[74,'C'],[84,'D'],[95,'E'],[108,'F'],[120,'G'],[132,'H'],[144,'I'],[153,'J']],
    hips:  [[90,'A/B'],[100,'C'],[110,'D'],[121,'E'],[134,'F'],[146,'G'],[158,'H'],[170,'I'],[179,'J']],
    fromUK: function(uk) {
      if (uk<=6)  return 'A/B'; if (uk<=10) return 'C'; if (uk<=14) return 'D';
      if (uk<=18) return 'E';   if (uk<=22) return 'F'; if (uk<=26) return 'G';
      if (uk<=30) return 'H';   if (uk<=34) return 'I'; return 'J';
    },
  };

  var CLOTHES_SIZES = {
    '4/6 (A/B)':  { chest:'77-82',   waist:'59-64',   hips:'85-90'   },
    '8/10 (C)':   { chest:'83-92',   waist:'65-74',   hips:'91-100'  },
    '12/14 (D)':  { chest:'93-102',  waist:'75-84',   hips:'101-110' },
    '16/18 (E)':  { chest:'103-113', waist:'85-95',   hips:'111-121' },
    '20/22 (F)':  { chest:'114-126', waist:'96-108',  hips:'122-134' },
    '24/26 (G)':  { chest:'127-138', waist:'109-120', hips:'135-146' },
    '28/30 (H)':  { chest:'139-150', waist:'121-132', hips:'147-158' },
    '32/34 (I)':  { chest:'151-162', waist:'133-144', hips:'159-170' },
    '36/38 (J)':  { chest:'163-171', waist:'145-153', hips:'171-179' },
  };

  var DENIM = {
    order: ['A','B','C1','C2','D1','D2','E1','E2','F1','F2','G1','G2','H1','H2','I1','I2','J1','J2'],
    waist: [[61,'A'],[64,'B'],[69,'C1'],[74,'C2'],[79,'D1'],[84,'D2'],[91,'E1'],[98,'E2'],[103,'F1'],[109,'F2'],[114,'G1'],[120,'G2'],[126,'H1'],[132,'H2'],[143,'I1'],[154,'I2'],[164,'J1'],[170,'J2']],
    hips:  [[87,'A'],[90,'B'],[95.5,'C1'],[100,'C2'],[105,'D1'],[110,'D2'],[116,'E1'],[121,'E2'],[128,'F1'],[134,'F2'],[140,'G1'],[146,'G2'],[154,'H1'],[161,'H2'],[169,'I1'],[180,'I2'],[190,'J1'],[196,'J2']],
    fromUK: {4:'A',6:'B',8:'C1',10:'C2',12:'D1',14:'D2',16:'E1',18:'E2',20:'F1',22:'F2',24:'G1',26:'G2',28:'H1',30:'H2',32:'I1',34:'I2',36:'J1',38:'J2'},
  };

  var UKBAND = { 'A/B':[4,6], C:[8,10], D:[12,14], E:[16,18], F:[20,22], G:[24,26], H:[28,30], I:[32,34], J:[36,38] };
  var LETTER_TO_UK = { 'A/B':6, C:10, D:14, E:18, F:22, G:26, H:30, I:34, J:38 };
  var LETTER_TO_TIGHTSBAND = { 'A/B':'4/6', C:'8/10', D:'12/14', E:'16/18', F:'20/22', G:'24/26', H:'28-36', I:'28-36', J:'28-36' };

  // Body-measurement bra sizing (underbust + bust at fullest, cm) — per the official
  // chart ("Suzanne's proposal"). Format: category → { underbustRange → { bustRange → snagSize } }.
  var BRA_SIZES = {
    regular: { '64-71':{'67-74':1},  '72-80':{'75-84':2},  '81-88':{'85-95':3},   '89-99':{'96-102':4},  '100-110':{'106-117':5}, '111-123':{'118-131':6}, '124-136':{'132-143':7}, '137-149':{'144-153':8}, '150-162':{'154-165':9} },
    full:    { '64-71':{'75-83':1},  '72-80':{'85-93':2},  '81-88':{'96-102':3},  '89-99':{'103-115':4}, '100-110':{'118-130':5}, '111-123':{'132-143':6}, '124-136':{'144-153':7}, '137-149':{'154-165':8}, '150-162':{'166-176':9} },
    grande:  { '64-71':{'84-91':1},  '72-80':{'94-102':2}, '81-88':{'103-111':3}, '89-99':{'116-129':4}, '100-110':{'131-143':5}, '111-123':{'144-153':6}, '124-136':{'154-165':7}, '137-149':{'166-176':8}, '150-162':{'177-187':9} },
  };

  var BRA_BAND_LOOKUP    = { '24-28':1,'28-32':2,'32-36':3,'36-40':4,'40-44':5,'44-50':6,'50-54':7,'54-60':8,'60-66':9 };
  var BRA_BAND_LOOKUP_EU = { '65-75':1,'75-85':2,'85-95':3,'95-105':4,'105-115':5,'115-130':6,'130-140':7,'140-155':8,'155-165':9 };
  // UK: DD/E/FF/GG notation  US/CA: DD/E as one option  EU/DE: single letters only
  var BRA_CUP_LOOKUP     = { AA:'regular',A:'regular',B:'regular',C:'regular',D:'full',DD:'full',E:'full',F:'full',FF:'grande',G:'grande',GG:'grande',H:'grande',HH:'grande',I:'grande',J:'grande',JJ:'grande',K:'grande' };
  var BRA_CUP_LOOKUP_US  = { AA:'regular',A:'regular',B:'regular',C:'regular',D:'regular','DD/E':'full',F:'full',G:'full',H:'full',I:'full',J:'full',K:'grande',L:'grande',M:'grande',N:'grande',O:'grande' };
  var BRA_CUP_LOOKUP_EU  = { AA:'regular',A:'regular',B:'regular',C:'regular',D:'regular',E:'full',F:'full',G:'full',H:'full',I:'full',J:'full',K:'grande',L:'grande',M:'grande',N:'grande',O:'grande' };
  var BRA_CUP_LABEL      = { regular:'Reg', full:'Full', grande:'Grande' };
  var FISHNETS        = { fabulousTightsLetters:['A/B','C','D'] };

  // Body measurement input bounds (cm, internal). Wider than size-chart min/max
  // so real bodies outside the catalog still pass; catches typos like 2000.
  var MEAS_LIMITS_CM = {
    bustCm:      { min: 50, max: 220 },
    waistCm:     { min: 45, max: 200 },
    hipsCm:      { min: 50, max: 220 },
    underbustCm: { min: 50, max: 200 },
    bustFullCm:  { min: 55, max: 220 },
  };

  // ─── STATE ────────────────────────────────────────────────────────────────

  var state = {
    screen:'intro', path:null, unit:'cm', region:'UK', domain:'',
    build:null, clothingMeasOpen:false, topSize:null, bottomSize:null, heightBand:null,
    heightCm:null, bustCm:null, waistCm:null, hipsCm:null,
    braMode:null, braBand:null, braCup:null,
    underbustCm:null, bustFullCm:null, topChoice:null, botChoice:null, result:null,
    emailDone:false,
    // Per-completion guards for the two "Completed Fit Key" routes (see below).
    completeSent:false, completeSentEmail:false,
    // Per-completion guard for the dedicated Fit Key Email Signup metric.
    signupSent:false,
    // Was this visitor ALREADY known to Klaviyo before this completion? Captured in
    // finishForm before any identify() of ours can create the cookie, so it reports
    // the state we found rather than the state we caused. Reported as `Known Visitor`:
    // false means a genuinely new profile, which is what new-subscriber reporting needs.
    knownVisitor:false,
  };

  var STEPS = ['intro','shape','sizes','height','addmeas','meas','bra','email','card'];

  // ─── HELPERS ──────────────────────────────────────────────────────────────

  function regCfg() { return REGIONS[state.region] || REGIONS.UK; }
  function regionShift() { return regCfg().regionShift || 0; }
  function rangeLookup(v, t) { for (var i=0; i<t.length; i++) { if (v<=t[i][0]) return t[i][1]; } return t[t.length-1][1]; }
  function largerByOrder(a, b, o) { return o.indexOf(a) >= o.indexOf(b) ? a : b; }
  function smallerByOrder(a, b, o) { return o.indexOf(a) <= o.indexOf(b) ? a : b; }
  function heightBandFromCm(cm) { if (cm<161) return 0; if (cm<=174) return 1; return 2; }
  function fishnetsFrom(t) { return FISHNETS.fabulousTightsLetters.indexOf(t) !== -1 ? 'Fabulous' : 'Stunning'; }
  function denimUKof(s) { return 4 + DENIM.order.indexOf(s)*2; }
  // Height has a FIXED per-region unit, independent of the cm/in body-measurement
  // toggle: feet/inches for UK·US·CA, centimetres for EU·DE·AU. (The toggle now only
  // affects body measurements, which is why it lives inside the measurements box.)
  var HEIGHT_UNIT = { UK:'in', US:'in', CA:'in', EU:'cm', DE:'cm', AU:'cm' };
  function heightUnit() { return HEIGHT_UNIT[state.region] || 'cm'; }
  function toCmU(v, u) { var n=parseFloat(v); if (isNaN(n)) return null; return u==='cm' ? n : n*2.54; }
  function toCm(v) { return toCmU(v, state.unit); }
  function dispU(cm, u) { if (cm===null||cm===undefined||cm==='') return ''; return u==='cm' ? String(Math.round(cm)) : String(Math.round(cm/2.54*10)/10); }
  function dispCm(cm) { return dispU(cm, state.unit); }
  function phxU(c, i, u) { var p = state.region==='DE' ? 'z. B. ' : 'e.g. '; return p + (u==='cm' ? c : i); }
  function phx(c, i) { return phxU(c, i, state.unit); }
  function root() { return document.getElementById('fkroot'); }

  function measLimitDisp(field) {
    var lim=MEAS_LIMITS_CM[field];
    if (!lim) return { min:'', max:'' };
    if (state.unit==='cm') return { min:lim.min, max:lim.max };
    return { min:Math.round(lim.min/2.54*10)/10, max:Math.round(lim.max/2.54*10)/10 };
  }

  function measInputAttrs(field) {
    var d=measLimitDisp(field);
    return 'min="'+d.min+'" max="'+d.max+'" step="any"';
  }

  function measInputHandlers(field) {
    return 'oninput="SnagFitKey.setMeas(\''+field+'\',this.value)" onchange="SnagFitKey.setMeas(\''+field+'\',this.value)"';
  }

  function syncMeasureFieldsFromDom() {
    // Reads whichever measurement fields are currently on-screen (the meas + bra
    // screens); the per-field `if (!el) continue` no-ops for fields not rendered.
    var map={bustCm:'fk-bust',waistCm:'fk-waist',hipsCm:'fk-hips',underbustCm:'fk-underbust',bustFullCm:'fk-bust-full'};
    for (var field in map) {
      if (!map.hasOwnProperty(field)) continue;
      var el=document.getElementById(map[field]);
      if (!el) continue;
      state[field]=el.value==='' ? null : toCm(el.value);
    }
    syncBustFullFromBust();
  }

  function measureCmInRange(field, cm) {
    if (cm===null||cm===undefined||cm==='') return false;
    var n=parseFloat(cm);
    if (isNaN(n)) return false;
    var lim=MEAS_LIMITS_CM[field];
    if (!lim) return n>0;
    return n>=lim.min&&n<=lim.max;
  }

  function optionalMeasureCmInRange(field, cm) {
    if (cm===null||cm===undefined||cm==='') return true;
    return measureCmInRange(field, cm);
  }

  // Only the measurement fields whose block is actually OPEN can be seen/edited, so
  // only those should ever trigger a range warning. A stale value left behind a
  // collapsed block (e.g. typed in inches, then the block closed) must not surface
  // an unfixable warning — and compute() ignores those values for the same reason.
  function activeMeasureFields() {
    var fields=[];
    if (state.clothingMeasOpen) fields.push('bustCm','waistCm','hipsCm');
    if (state.braMode==='figure') fields.push('underbustCm','bustFullCm');
    return fields;
  }

  function showMeasureRangeWarning() {
    var f=activeMeasureFields();
    for (var i=0;i<f.length;i++) {
      var cm=state[f[i]];
      if (cm!==null&&cm!==undefined&&cm!==''&&!measureCmInRange(f[i],cm)) return true;
    }
    return false;
  }

  // Warning text for out-of-range measurements. If an out-of-range value would be
  // perfectly valid under the OTHER unit (e.g. "98" entered as inches but meant as
  // cm), the user almost certainly has the wrong unit selected — so point them at
  // the cm/inches toggle instead of the generic "looks unusual" message.
  function measureWarnMessage() {
    var l=getLang();
    var fields=activeMeasureFields();
    var anyBad=false, unitMismatch=false;
    for (var i=0;i<fields.length;i++) {
      var f=fields[i], cm=state[f];
      if (cm===null||cm===undefined||cm==='') continue;
      if (measureCmInRange(f, cm)) continue;
      anyBad=true;
      var asOtherUnit=state.unit==='in' ? cm/2.54 : cm*2.54;
      if (measureCmInRange(f, asOtherUnit)) unitMismatch=true;
    }
    if (!anyBad) return '';
    if (unitMismatch) return state.unit==='in' ? l.meas_hint_cm : l.meas_hint_in;
    return l.meas_range_error;
  }

  // Optional measurements are valid unless an ACTIVE (visible) field is filled with
  // an out-of-range value. Any field may be left blank — incomplete just means no
  // refinement, never a hard block. Fields behind a collapsed block are ignored, so
  // a stale value can't silently disable submit (it lines up with the warning).
  function clothingMeasOptionalValid() {
    var f=activeMeasureFields();
    for (var i=0;i<f.length;i++) {
      if (!optionalMeasureCmInRange(f[i], state[f[i]])) return false;
    }
    return true;
  }

  function clothesLetterFromMeasure(chest, waist, hips) {
    if (!chest||!waist||!hips) return null;
    for (var k in CLOTHES_SIZES) {
      if (!CLOTHES_SIZES.hasOwnProperty(k)) continue;
      var r=CLOTHES_SIZES[k];
      var c=r.chest.split('-').map(Number), w=r.waist.split('-').map(Number), h=r.hips.split('-').map(Number);
      if (chest>=c[0]&&chest<=c[1]&&waist>=w[0]&&waist<=w[1]&&hips>=h[0]&&hips<=h[1]) {
        var m=k.match(/\(([^)]+)\)/); return m?m[1]:null;
      }
    }
    return null;
  }

  function braFromMeasure(underbustCm, bustCm) {
    if (underbustCm<62||bustCm<65) return null;
    var prefs=['grande','full','regular'];
    for (var p=0; p<prefs.length; p++) {
      var cup=prefs[p];
      for (var ur in BRA_SIZES[cup]) {
        if (!BRA_SIZES[cup].hasOwnProperty(ur)) continue;
        var u=ur.split('-').map(Number);
        if (underbustCm>=u[0]&&underbustCm<=u[1]) {
          for (var br in BRA_SIZES[cup][ur]) {
            if (!BRA_SIZES[cup][ur].hasOwnProperty(br)) continue;
            var b=br.split('-').map(Number);
            if (bustCm>=b[0]&&bustCm<=b[1]) return BRA_SIZES[cup][ur][br]+' '+BRA_CUP_LABEL[cup];
          }
        }
      }
    }
    return null;
  }

  function braFromKnown(bandVal, cupLetter) {
    var euDe=state.region==='EU'||state.region==='DE';
    var usca=state.region==='US'||state.region==='CA';
    var cupTable=euDe ? BRA_CUP_LOOKUP_EU : (usca ? BRA_CUP_LOOKUP_US : BRA_CUP_LOOKUP);
    var bandTable=euDe ? BRA_BAND_LOOKUP_EU : BRA_BAND_LOOKUP;
    var cupPref=cupTable[(cupLetter||'').toUpperCase()];
    if (!cupPref) return null;
    // AU shows dress-size bands (8/10/12…) but maps onto the UK inch bands: AU + 22 = UK
    // (AU 10 = UK 32, AU 12 = UK 34 …). So convert, then use the shared UK band table.
    if (state.region==='AU') bandVal=bandVal+22;
    // The band ranges deliberately overlap at the boundaries (…28-32, 32-36…). At a
    // boundary, recommend the LARGER size — so don't break on the first match; keep
    // the last (largest) matching band. e.g. 32 → 3 (not 2), 36 → 4.
    var snagSize=1;
    for (var range in bandTable) {
      if (!bandTable.hasOwnProperty(range)) continue;
      var parts=range.split('-').map(Number);
      if (bandVal>=parts[0]&&bandVal<=parts[1]) snagSize=bandTable[range];
    }
    return snagSize+' '+BRA_CUP_LABEL[cupPref];
  }

  // ─── LANGUAGE (German for DE, English everywhere else) ───────────────────

  function getLang() {
    var de = state.region === 'DE';
    var hello = supportEmail();
    return {
      // Intro
      kick:            de ? 'Größen 32–66 · An echten Körpern getestet'
                         : (state.region==='US'||state.region==='CA') ? 'Sizes 0–34 · Fit-tested on real bodies'
                         : (state.region==='EU')                       ? 'Sizes 32–66 · Fit-tested on real bodies'
                         :                                               'Sizes 4–38 · Fit-tested on real bodies',
      intro_h1:        de ? 'Schluss mit Größentabellen.<br><span class="fk-hl">Finde deine Snag-Größe.</span>'
                          : 'Skip the sizing charts.<br><span class="fk-hl">Find your Snag Size.</span>',
      intro_body:      de ? 'Beantworte ein paar kurze Fragen und wir finden deine Snag-Größe. Sie wird auf deinem Gerät gespeichert, damit das Shoppen bei jedem Besuch einfacher ist.'
                          : 'Answer a few quick questions and we will find your Snag Size. It saves to your device for easier shopping every time you visit.',
      cta_start:       de ? 'Meine Snag-Größe finden'                                                : 'Find my Snag Size',
      // Navigation
      back:            de ? '← Zurück'                                                               : '← Back',
      cta_continue:    de ? 'Weiter'                                                                 : 'Continue',
      // Screen — usual sizes
      sizes_q:         de ? 'Welche Größen trägst du normalerweise?'                                 : 'Which sizes do you usually wear?',
      // Screen — add measurements?
      addmeas_h1:      de ? 'Möchtest du deine Körpermaße für eine genauere Kleidergröße hinzufügen?' : 'Would you like to add body measurements for a more accurate clothing size?',
      addmeas_body:    de ? 'Besonders empfohlen, wenn du Jeans oder einen BH kaufst!'               : "Most recommended if you're shopping for denim jeans or a bra!",
      addmeas_yes:     de ? 'Ja, meine Maße hinzufügen'                                              : 'Yes, add my measurements',
      addmeas_skip:    de ? 'Überspringen, meine Größen anzeigen'                                    : 'Skip — show my sizes',
      // Screen 2 — Sizing type
      sizing_h1:       de ? 'Nach welcher Kleidung suchst du normalerweise?'                         : 'Which clothes do you usually shop for?',
      sizing_body:     de ? 'Wähle das Größensystem, das du meistens nutzt.'                         : 'Pick the size system you normally use.',
      women_label:     de ? 'Damen-Größen'                                                           : "Women's sizing",
      women_sub:       de ? 'Strumpfhosen, Röcke, Denim, BHs und Kleidung in Snag-Buchstabengrößen' : 'Tights, skirts, denim, bras, and clothing in Snag letter sizes',
      men_label:       de ? 'Herren-Größen'                                                          : "Men's sizing",
      men_sub:         de ? 'Größen basierend auf deiner Bundweite'                                  : 'Sizing based on your waist',
      // Screen 3 — Path
      path_h1:         de ? 'Wie sollen wir deine Größe ermitteln?'                                  : 'How should we figure it out?',
      path_body:       de ? 'Beide Wege dauern weniger als eine Minute.'                             : 'Either way takes less than a minute.',
      usual_label_w:   de ? 'Ich kenne meine üblichen Größen'                                        : 'I know the sizes I usually wear',
      usual_sub_w:     de ? 'Super schnell. Nur deine normale Größe für Ober- und Unterteile.'       : 'Super quick. Just your usual top and bottom size.',
      usual_label_m:   de ? 'Ich kenne meine üblichen Größen'                                        : 'I know my usual sizes',
      usual_sub_m:     de ? 'Super schnell. Nur deine normale Bundweite.'                            : 'Super quick. Just your usual waist size.',
      measure_label:   de ? 'Ich trage meine genauen Maße ein'                                       : 'I will add my exact measurements',
      measure_sub:     de ? 'Der genaueste Weg zur perfekten Passform'                               : 'The most accurate way to do it',
      // Screen 4 — Form
      form_h1_usual:   de ? 'Nur ein paar kurze Fragen'                                              : 'Just a few quick questions',
      form_h1_measure: de ? 'Trage deine Maße ein'                                                   : 'Add your measurements',
      hint_usual_m:    de ? 'Wähle aus, was dir gut passt.'                                          : 'Choose what fits you well.',
      hint_usual_w:    de ? 'Wähle die Größe, die dir am besten passt.'                              : 'Choose the size that fits you best.',
      hint_measure:    de ? 'Lege das Maßband eng, aber bequem an.'                                  : 'Keep the tape measure snug but not too tight.',
      meas_decimal_note: de ? 'Dezimalzahlen sind in Ordnung – gib z. B. 80 oder 80,5 ein. Wir runden auf die nächste Zahl, um deine Größe zu finden.'
                            : 'Decimals are fine — enter 80 or 80.5. We round to the nearest number to find your size.',
      meas_range_error: de ? 'Bitte überprüfe deine Maße — dieser Wert liegt außerhalb des üblichen Bereichs.'
                            : 'Please double-check — that measurement looks outside a usual range.',
      meas_hint_cm:    de ? 'Das sieht nach Zentimetern aus — stell den Schalter oben auf cm.'        : 'Those look like centimetres — switch the unit toggle above to cm.',
      meas_hint_in:    de ? 'Das sieht nach Inch aus — stell den Schalter oben auf Inch.'             : 'Those look like inches — switch the unit toggle above to inches.',
      lbl_sizes:       de ? 'Größen'                                                                 : 'Sizes',
      lbl_units:       de ? 'Einheiten'                                                              : 'Units',
      lbl_inches:      de ? 'Inch'                                                                   : 'inches',
      // Shape question
      shape_q:         de ? 'Wie würdest du deine natürliche Form beschreiben?'                      : 'How would you describe your natural shape?',
      shape_hint:      de ? 'Das hilft uns herauszufinden, ob du etwas mehr Platz an Po und Oberschenkeln brauchst.' : 'This helps us see if you need a touch of extra room through your bum and thighs.',
      shape_curvy:     de ? 'Kurviger an Po und Oberschenkeln'                                       : 'Curvier through my bum and thighs',
      shape_curvy_sub: de ? 'Ein ausgeprägterer Po, vollere Oberschenkel oder beides'               : 'A fuller bum, thighs, or both',
      shape_straight:  de ? 'Gerader an Po und Oberschenkeln'                                        : 'Straighter through my bum and thighs',
      shape_str_sub:   de ? 'Ein flacherer Po oder schmalere Oberschenkel'                           : 'A flatter bum or narrower thighs',
      // Height
      height_q:        de ? 'Wie groß bist du?'                                                      : 'How tall are you?',
      // Size fields
      fld_top:         de ? 'Übliche Größe OBERTEILE'                                                : 'Usual TOP size',
      fld_bottom:      de ? 'Übliche Größe UNTERTEILE'                                               : 'Usual BOTTOM size',
      choose:          de ? 'Auswählen...'                                                           : 'Choose...',
      fld_waist_in:    de ? 'Übliche Bundweite (cm)'
                         : (state.region==='EU') ? 'Usual waist size (cm)'
                         :                         'Usual waist size (inches)',
      fld_chest_in:    de ? 'Übliche Brustweite (cm) · optional'
                         : (state.region==='EU') ? 'Usual chest size (cm) · optional'
                         :                         'Usual chest size (inches) · optional',
      skip_choose:     de ? 'Überspringen / Auswählen...'                                            : 'Skip / choose...',
      // Measure fields
      fld_height:      de ? 'Körpergröße'                                                            : 'Height',
      fld_bust:        de ? 'Brustumfang'                                                            : 'bust / chest',
      fld_chest_men:   de ? 'Brustumfang'                                                            : 'Chest',
      fld_waist:       de ? 'Taillenumfang'                                                          : 'Waist',
      fld_hips:        de ? 'Hüftumfang'                                                             : 'Hips',
      cta_show:        de ? 'Zeig mir meine Größen'                                                  : 'Show me my sizes',
      // Bra block
      bra_title:       de ? 'BH-Größe'                                                               : 'Bra size',
      bra_optional:    de ? '(optional)'                                                             : '(optional)',
      bra_know:        de ? 'Ich kenne meine BH-Größe'                                               : 'I know my bra size',
      bra_help:        de ? 'Hilf mir, sie zu finden'                                                : 'Help me figure it out',
      bra_band:        de ? 'Unterbrust'                                                             : 'Band',
      bra_cup:         de ? 'Körbchen'                                                               : 'Cup',
      bra_hint_known:  de ? 'Wenn du zum Beispiel eine 75C trägst, rechnen wir das direkt in deine perfekte Snag-BH-Größe um.'
                         : (state.region==='EU') ? 'For example, if you wear a 75C, we will convert that straight into your perfect Snag bra size.'
                         :                         'For example, if you wear a 34C, we will convert that straight into your perfect Snag bra size.',
      bra_underbust:   de ? 'Unterbrust, eng unter deinen Brüsten gemessen'                          : 'Underbust, snug under your boobs',
      bra_bust_full:   de ? 'Oberbrust, an der vollsten Stelle gemessen'                             : 'Bust, around the fullest part',
      bra_hint_figure: de ? 'Dein Unterbrustumfang verrät uns deine Bandgröße und dein Oberbrustumfang zeigt uns, ob du Regular, Full oder Grande bist.'
                          : 'Your underbust measurement tells us your band size, and your bust measurement tells us if you are Regular, Full, or Grande.',
      // Card
      card_h1:         de ? 'Deine Snag-Größe'                                                       : 'Your Snag Size',
      card_lead:       de ? 'Deine Snag-Größe ist…'                                                  : 'Your Snag Size Is…',
      card_tag:        de ? 'Meine Snag-Größe'                                                       : 'My Snag Size',
      card_hero_lab:   de ? 'Strumpfhosen und Chub Rub Shorts'                                       : 'Tights and Chub Rub Shorts',
      card_region_sfx: de ? '-Größen'                                                                : ' sizes',
      card_mens:       de ? 'Herren'                                                                 : "men's",
      grid_fishnets:   de ? 'Netzstrumpfhosen'                                                       : 'Fishnets',
      grid_bras:       de ? '<span class="fk-nocaps">BHs</span>'                                     : 'Bras',
      grid_tops:       de ? 'Oberteile'                                                              : 'Tops',
      grid_bottoms:    de ? 'Unterteile'                                                             : 'Bottoms',
      grid_dresses:    de ? 'Kleider'                                                                : 'Dresses',
      grid_denim:      de ? 'Denim & Tailoring'                                                      : 'Denim and Tailoring',
      grid_waist:      de ? 'Herren-Bundweite'                                                       : "Men's Waist",
      card_return:     de ? 'Nur 2 % Rücksendequote · Passgenau'                                     : 'Only a 2% return rate · True to size',
      cta_close:       de ? 'Zurück zum Shop'                                                        : 'Back to shopping',
      cta_edit_meas:   de ? 'Antworten bearbeiten'                                                   : 'Edit my answers',
      cta_email_me:    de ? 'Meine Größen per E-Mail senden'                                         : 'Send my sizes in an email',
      clothing_meas_title:  de ? 'Genaue Maße'                                                       : 'Exact measurements',
      clothing_meas_toggle: de ? 'Maße für genaue Kleidergrößen hinzufügen'                          : 'Add measurements for precise clothing sizes',
      clothing_meas_hint:   de ? 'Für Strumpfhosen verwenden wir die oben gewählte Größe. Gib alle drei Maße ein, um Oberteil-, Kleider- und Denim-Größen zu verfeinern.'
                              : 'For stretchy tights we use the size you picked above. Enter all three to fine-tune your top, dress and denim sizes.',
      fit_help:        de ? 'Deine Maße liegen über mehr als eine Größe, daher kann deine perfekte Größe je nach Artikel variieren – besonders bei Kleidern und figurbetonten Styles. Schau in die Größentabelle des jeweiligen Produkts oder schreib uns deine Maße an <a href="mailto:'+hello+'">'+hello+'</a> und wir helfen dir, die beste Passform zu finden.'
                         : 'Your measurements span more than one size, so your perfect fit can vary by item — especially for dresses and fitted styles. Check the size guide on the product you want, or email your measurements to <a href="mailto:'+hello+'">'+hello+'</a> and we will help you find the best fit.',
      meas_recommend:  de ? 'Für die genaueste Kleidergröße empfehlen wir, deine genauen Maße einzugeben.'
                          : 'We recommend checking exact measurements for the most accurate clothing size.',
    };
  }

  // ─── COMPUTE ──────────────────────────────────────────────────────────────

  function compute() {
    var topRaw=null, botRaw, heightBand=state.heightBand, denimRaw, topFitHelp=false;

    var t=parseInt(state.topSize,10), b=parseInt(state.bottomSize,10);
    // Tights + bottoms ALWAYS come from the usual BOTTOM size. Tights are a
    // stretch garment gated by thigh/hip fit, which the size a customer already
    // wears captures and raw waist/hip numbers do not — so tights never derive
    // from measurements. This is the whole point of the dress-size-first flow.
    botRaw=CLOTHES.fromUK(b);
    // Exact measurements are an OPTIONAL refinement for fitted clothing only
    // (tops, dresses, denim). Used only when the block is open and all three are
    // in range; otherwise we fall back to the usual top/bottom sizes.
    var hasMeas = state.clothingMeasOpen
      && measureCmInRange('bustCm',state.bustCm)
      && measureCmInRange('waistCm',state.waistCm)
      && measureCmInRange('hipsCm',state.hipsCm);
    if (hasMeas) {
      var bustL2=rangeLookup(state.bustCm,CLOTHES.chest);
      var wL2=rangeLookup(state.waistCm,CLOTHES.waist);
      // Tops follow bust + waist. Within 1 size → take the larger; more than 1
      // size apart → product-dependent, so flag for fit help instead of guessing.
      var topSpan=Math.abs(CLOTHES.order.indexOf(bustL2)-CLOTHES.order.indexOf(wL2));
      if (topSpan<=1) topRaw=largerByOrder(bustL2,wL2,CLOTHES.order);
      else { topRaw=null; topFitHelp=true; }
      denimRaw=smallerByOrder(rangeLookup(state.waistCm,DENIM.waist),rangeLookup(state.hipsCm,DENIM.hips),DENIM.order);
    } else {
      topRaw=CLOTHES.fromUK(t);
      denimRaw=DENIM.fromUK[b];
    }

    var topL=topRaw;
    var botL=botRaw;
    var matrix=tightsBuild()==='curvy' ? TIGHTS.curvy : TIGHTS.athletic;
    var tights=matrix[LETTER_TO_TIGHTSBAND[botL]][heightBand];
    var fishnets=fishnetsFrom(tights);

    // Dresses are a FULL-BODY garment, so a dress must fit both the top and the
    // bottom. If the top and bottom are within one size, use the larger; if they
    // span 2+ sizes there is no single size that fits well → leave the dress blank
    // and flag for fit help (check the product size guide / email fittings).
    var dress=null;
    if (topL && botL) {
      var dti=CLOTHES.order.indexOf(topL), dbi=CLOTHES.order.indexOf(botL);
      if (dti>=0 && dbi>=0 && Math.abs(dti-dbi)<=1) dress=largerByOrder(topL,botL,CLOTHES.order);
      else topFitHelp=true;
    } else {
      topFitHelp=true;
    }

    var bra='-';
    if (state.braMode==='known'   && state.braBand && state.braCup)              bra=braFromKnown(parseInt(state.braBand,10),state.braCup)||'-';
    else if (state.braMode==='figure' && state.underbustCm) {
      var bustFull=state.bustFullCm||state.bustCm;
      if (bustFull) bra=braFromMeasure(state.underbustCm,bustFull)||'-';
    }
    return { tights:tights, fishnets:fishnets, top:topL, bottom:botL, dress:dress, denim:denimRaw, bra:bra, fitHelp:topFitHelp, usedMeas:hasMeas, region:state.region, unit:state.unit };
  }

  // ─── WIZARD UI ────────────────────────────────────────────────────────────

  function rnum(uk) { return uk+regionShift(); }
  function letterBand(l) { var b=UKBAND[l]; return state.region+' '+rnum(b[0])+'/'+rnum(b[1]); }
  // Region-specific ("local"/country) clothing sizes for Klaviyo, mirroring the
  // size-card subtitles: clothing letters → "EU 36/38" via letterBand; denim uses
  // its own UK-number map. Returns '' for absent/unknown so we never blank a prop.
  function localClothingSize(l) { return (l && UKBAND[l]) ? letterBand(l) : ''; }
  function localDenimSize(s) { return s ? (state.region + ' ' + rnum(denimUKof(s))) : ''; }

  // Build Klaviyo size props from a result, keyed with `prefix` ("Snag " for profile
  // properties, "" for event props). Skips empty and the "-"/"–" sentinels compute()
  // uses for "no size" — so a partial retake (e.g. no bra) never overwrites an
  // existing profile property with a blank or a dash. Omitted keys are left untouched.
  function fkSizeProps(r, prefix) {
    var p = {};
    function set(k, v) { if (v && v !== '-' && v !== '–') p[prefix + k] = v; }
    set('Tights Size', r.tights);
    set('Fishnets Size', r.fishnets);
    set('Tops Size', r.top);
    set('Bottoms Size', r.bottom);
    set('Dress Size', r.dress);
    set('Denim Size', r.denim);
    set('Bra Size', r.bra);
    if (r.top    && r.top    !== '-') set('Tops Size (Local)',    localClothingSize(r.top));
    if (r.bottom && r.bottom !== '-') set('Bottoms Size (Local)', localClothingSize(r.bottom));
    if (r.dress  && r.dress  !== '-') set('Dress Size (Local)',   localClothingSize(r.dress));
    if (r.denim  && r.denim  !== '-') set('Denim Size (Local)',   localDenimSize(r.denim));
    return p;
  }

  // Client-side Klaviyo push (onsite window.klaviyo, else the legacy _learnq queue).
  function fkKlaviyoPush(args) {
    var kl = global.klaviyo || null;
    if (kl && typeof kl.push === 'function') kl.push(args);
    else { var q = global._learnq = global._learnq || []; q.push(args); }
  }
  // Fire "Completed Fit Key" once per completion. Two routes, because neither one
  // works in every case:
  //   • trackFitKeyComplete()        — onsite track, attached via the browser's
  //     __kla_id cookie. Correct for skippers and already-identified visitors (their
  //     cookie is already linked to a profile server-side), but it drops/misattaches
  //     for a brand-NEW subscriber whose cookie isn't linked yet. Used only when we
  //     have no email in hand.
  //   • trackFitKeyCompleteByEmail() — client/events POST carrying the email in the
  //     profile block, so it lands deterministically on the profile we just
  //     identified. Used on email submit. Mirrors the Step-2 subscribe call;
  //     browser-origin client/* calls pass Klaviyo bot protection.
  // Each route is guarded by its own per-completion flag, so the normal paths
  // (submit / skip / auto-skip) fire exactly one event. Only the rare
  // skip-then-subscribe path fires both: the anonymous one, then the email-keyed one
  // — which is the one that must never be missed, so a duplicate beats a no-show.
  // `path` records WHICH of the three completion routes fired, so one metric can be
  // split in reporting: 'email_submit' | 'skipped' | 'known_visitor'. Without it every
  // route looked identical in Klaviyo and form submissions were uncountable.
  function fkCompleteEventProps(path) {
    var r = state.result || {};
    var e = {
      Region: r.region || state.region,
      Path: path,
      'Known Visitor': !!state.knownVisitor
    };
    Object.assign(e, fkSizeProps(r, ''));
    return e;
  }
  // Shared client/events POST. Browser-origin client/* calls carry only the public key
  // and pass Klaviyo bot protection. Keyed by EMAIL so the event lands on the profile
  // deterministically, even when that profile was created moments ago.
  function fkClientEvent(publicKey, metricName, props, email) {
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(publicKey), {
        method: 'POST',
        // keepalive lets the request outlive the page. Without it a shopper who closes the
        // tab or navigates away mid-flight loses the event entirely — observed in testing.
        // Payload is far below the 64KB keepalive ceiling.
        keepalive: true,
        headers: { 'content-type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: props,
              metric: { data: { type: 'metric', attributes: { name: metricName } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        })
      }).catch(function(){}); // silent fail — always advance to result
    } catch(ex){}
  }
  function trackFitKeyComplete(path) {
    if (state.completeSent) return;
    state.completeSent = true;
    try { fkKlaviyoPush(['track', 'Completed Fit Key', fkCompleteEventProps(path)]); } catch(ex){}
  }
  function trackFitKeyCompleteByEmail(email) {
    var kv = ((global.snagFitKeyConfig || {}).klaviyo) || {};
    // No public key or no email → fall back to the onsite route rather than lose it.
    if (!kv.publicKey || !email) { trackFitKeyComplete('email_submit'); return; }
    if (state.completeSentEmail) return;
    state.completeSentEmail = true;
    fkClientEvent(kv.publicKey, 'Completed Fit Key', fkCompleteEventProps('email_submit'), email);
  }
  // Dedicated signup metric — fires ONLY on a real email submit, never on skip or
  // auto-skip. Separate from Completed Fit Key so signups can be charted directly in
  // Klaviyo's Metrics view rather than reconstructed by filtering one mixed metric.
  // Signup rate = Fit Key Email Signup ÷ Completed Fit Key.
  function trackFitKeyEmailSignup(email) {
    if (state.signupSent) return;
    var kv = ((global.snagFitKeyConfig || {}).klaviyo) || {};
    if (!kv.publicKey || !email) return;
    state.signupSent = true;
    var p = fkCompleteEventProps('email_submit');
    p['Signup Source'] = FK_SIGNUP_SOURCE;
    fkClientEvent(kv.publicKey, 'Fit Key Email Signup', p, email);
  }

  function setBar() {
    var bar=document.getElementById('bar');
    if (!bar) return;
    var i=STEPS.indexOf(state.screen);
    var pct=(i/(STEPS.length-1))*100;
    bar.style.clipPath='inset(0 '+(100-pct)+'% 0 0)';
  }

  function opt(label, sub, on, sel) {
    return '<button type="button" class="fk-opt" aria-pressed="'+(sel?'true':'false')+'" onclick="'+on+'">'+label+(sub?'<small>'+sub+'</small>':'')+'</button>';
  }

  function unitSeg() {
    var l=getLang();
    return '<div class="fk-unitrow"><span class="fk-lbl">'+l.lbl_units+'</span><span class="fk-unitseg"><button type="button" aria-pressed="'+(state.unit==='cm'?'true':'false')+'" onclick="SnagFitKey.setUnit(\'cm\')">cm</button><button type="button" aria-pressed="'+(state.unit==='in'?'true':'false')+'" onclick="SnagFitKey.setUnit(\'in\')">'+l.lbl_inches+'</button></span></div>';
  }

  function regionSeg() {
    var l=getLang();
    var btns=REGION_TOGGLE.map(function(r) {
      return '<button type="button" aria-pressed="'+(state.region===r?'true':'false')+'" onclick="SnagFitKey.setRegion(\''+r+'\')">'+r+'</button>';
    }).join('');
    return '<div class="fk-unitrow"><span class="fk-lbl">'+l.lbl_sizes+'</span><span class="fk-unitseg">'+btns+'</span></div>';
  }

  function braBlock(bare) {
    var l=getLang();
    var euDe=state.region==='EU'||state.region==='DE';
    var usca=state.region==='US'||state.region==='CA';
    // AU uses dress-size bands (8/10/12…); the engine converts AU + 22 → UK for lookup.
    var bands=state.region==='AU' ? [6,8,10,12,14,16,18,20,22,24,26,28,30,32,34,36]
            : euDe ? [60,65,70,75,80,85,90,95,100,105,110,115]
            : [28,30,32,34,36,38,40,42,44,46,48,50,52,54,56,58];
    var cupTable=euDe ? BRA_CUP_LOOKUP_EU : (usca ? BRA_CUP_LOOKUP_US : BRA_CUP_LOOKUP);
    var cups=Object.keys(cupTable);
    var inner='';
    if (state.braMode==='known') {
      inner='<div class="fk-row2"><div><label class="fk-fld">'+l.bra_band+'</label><select onchange="SnagFitKey.state.braBand=this.value"><option value="">–</option>'+
        bands.map(function(b){return '<option '+(state.braBand==b?'selected':'')+'>'+b+'</option>';}).join('')+
        '</select></div><div><label class="fk-fld">'+l.bra_cup+'</label><select onchange="SnagFitKey.state.braCup=this.value"><option value="">–</option>'+
        cups.map(function(c){return '<option '+(state.braCup===c?'selected':'')+'>'+c+'</option>';}).join('')+
        '</select></div></div><p class="fk-hint">'+l.bra_hint_known+'</p>';
    } else if (state.braMode==='figure') {
      var l_=getLang();
      var u=state.unit==='cm'?'cm':l_.lbl_inches;
      inner=unitSeg()+
        '<div class="fk-meas-field"><label class="fk-fld" for="fk-underbust">'+l.bra_underbust+' ('+u+')</label><input type="number" id="fk-underbust" inputmode="decimal" '+measInputAttrs('underbustCm')+' placeholder="'+phx('80','32')+'" value="'+dispCm(state.underbustCm)+'" '+measInputHandlers('underbustCm')+'></div>'+
        '<div class="fk-meas-field"><label class="fk-fld" for="fk-bust-full">'+l.bra_bust_full+' ('+u+')</label><input type="number" id="fk-bust-full" inputmode="decimal" '+measInputAttrs('bustFullCm')+' placeholder="'+phx('100','39')+'" value="'+dispCm(state.bustFullCm)+'" '+measInputHandlers('bustFullCm')+'></div>'+
        '<p class="fk-hint">'+l.bra_hint_figure+'</p>';
    }
    var body='<div class="fk-minitoggle">'+
      '<button type="button" aria-pressed="'+(state.braMode==='known'?'true':'false')+'" onclick="SnagFitKey.setBraMode(\'known\')">'+l.bra_know+'</button>'+
      '<button type="button" aria-pressed="'+(state.braMode==='figure'?'true':'false')+'" onclick="SnagFitKey.setBraMode(\'figure\')">'+l.bra_help+'</button>'+
      '</div>'+inner;
    // bare = fields only (dedicated screen supplies its own heading); default wraps
    // the block in a titled subcard (kept for any legacy inline use).
    if (bare) return body;
    return '<div class="fk-subcard"><div class="fk-q">'+l.bra_title+' <span class="fk-muted">'+l.bra_optional+'</span></div>'+body+'</div>';
  }


  // The fk-fade entrance animation should only play when moving between screens.
  // In-form clicks (shape, height, unit, region, bra mode) re-render the same
  // screen — replaying the slide-up there causes a visible jolt, so we suppress it.
  var lastScreen = null;

  function render() {
    // Defensive: never leave the panel blank on an unknown/removed screen.
    if (STEPS.indexOf(state.screen) === -1) state.screen='intro';
    setBar();
    var s=state.screen;
    var R=root(); if (!R) return;
    var sameScreen = (s === lastScreen);
    lastScreen = s;
    var ov=document.getElementById('fkoverlay'); if (ov) ov.dataset.screen=s;
    var l=getLang();
    var cfg=regCfg(), sizes=cfg.retailSizes||[];
    var domain=brandDomain();

    // Show/hide the static email step div alongside #fkroot based on screen.
    // Must use explicit 'block' not '' — '' removes the inline style and lets
    // the CSS .fk-email-step { display: none } rule take back over.
    var emailStep = document.getElementById('fk-email-step');
    if (emailStep) emailStep.style.display = (s === 'email') ? 'block' : 'none';
    R.style.display = (s === 'email') ? 'none' : 'block';

    if (s==='intro') {
      R.innerHTML='<div class="fk-screen fk-screen--intro"><div class="fk-kick">'+l.kick+'</div><h1 class="fk-title fk-title--hero">'+l.intro_h1+'</h1><p class="fk-lede fk-lede--intro">'+l.intro_body+'</p><button type="button" class="fk-btn" onclick="SnagFitKey.pick()">'+l.cta_start+'</button></div>';

    // ── One question per screen. Each screen: Back · one focal question · Continue ──
    } else if (s==='shape') {
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'intro\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.shape_q+'</h1><p class="fk-hint">'+l.shape_hint+'</p>'+
        '<div class="fk-opts">'+
          opt(l.shape_curvy,l.shape_curvy_sub,"SnagFitKey.setBuild('curvy')",state.build==='curvy')+
          opt(l.shape_straight,l.shape_str_sub,"SnagFitKey.setBuild('athletic')",state.build==='athletic')+
        '</div>'+
        '<button type="button" class="fk-btn" id="fk-next"'+(screenValid('shape')?'':' disabled')+' onclick="SnagFitKey.go(\'sizes\')">'+l.cta_continue+'</button></div>';

    } else if (s==='sizes') {
      var selR=function(v,arr,ph) {
        return '<select onchange="SnagFitKey.state.'+v+'=this.value;SnagFitKey.chk()"><option value="">'+ph+'</option>'+
          arr.map(function(n){return '<option value="'+n+'" '+(state[v]==n?'selected':'')+'>'+rnum(n)+'</option>';}).join('')+'</select>';
      };
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'shape\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.sizes_q+'</h1><p class="fk-hint">'+l.hint_usual_w+'</p>'+
        regionSeg()+
        '<label class="fk-fld">'+l.fld_top+' ('+state.region+')</label>'+selR('topSize',sizes,l.choose)+
        '<label class="fk-fld">'+l.fld_bottom+' ('+state.region+')</label>'+selR('bottomSize',sizes,l.choose)+
        '<button type="button" class="fk-btn" id="fk-next"'+(screenValid('sizes')?'':' disabled')+' onclick="SnagFitKey.go(\'height\')">'+l.cta_continue+'</button></div>';

    } else if (s==='height') {
      var HL=(cfg.heightLabels && cfg.heightLabels[heightUnit()]) || cfg.heightLabels.cm;
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'sizes\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.height_q+'</h1>'+
        '<div class="fk-opts">'+
          opt(HL[0],'','SnagFitKey.setH(0)',state.heightBand===0)+
          opt(HL[1],'','SnagFitKey.setH(1)',state.heightBand===1)+
          opt(HL[2],'','SnagFitKey.setH(2)',state.heightBand===2)+
        '</div>'+
        '<button type="button" class="fk-btn" id="fk-next"'+(screenValid('height')?'':' disabled')+' onclick="SnagFitKey.go(\'addmeas\')">'+l.cta_continue+'</button></div>';

    } else if (s==='addmeas') {
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'height\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.addmeas_h1+'</h1><p class="fk-lede">'+l.addmeas_body+'</p>'+
        '<button type="button" class="fk-btn" onclick="SnagFitKey.chooseAddMeas(true)">'+l.addmeas_yes+'</button>'+
        '<button type="button" class="fk-btn fk-btn--secondary" onclick="SnagFitKey.chooseAddMeas(false)">'+l.addmeas_skip+'</button></div>';

    } else if (s==='meas') {
      var um=state.unit==='cm'?'cm':l.lbl_inches;
      var mw=measureWarnMessage();
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'addmeas\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.form_h1_measure+'</h1><p class="fk-hint">'+l.hint_measure+'</p>'+
        unitSeg()+
        '<div class="fk-row3">'+
          '<div class="fk-meas-field"><label class="fk-fld" for="fk-bust">'+l.fld_bust+' ('+um+')</label><input type="number" id="fk-bust" inputmode="decimal" '+measInputAttrs('bustCm')+' placeholder="'+phx('98','39')+'" value="'+dispCm(state.bustCm)+'" '+measInputHandlers('bustCm')+'></div>'+
          '<div class="fk-meas-field"><label class="fk-fld" for="fk-waist">'+l.fld_waist+' ('+um+')</label><input type="number" id="fk-waist" inputmode="decimal" '+measInputAttrs('waistCm')+' placeholder="'+phx('80','31')+'" value="'+dispCm(state.waistCm)+'" '+measInputHandlers('waistCm')+'></div>'+
          '<div class="fk-meas-field"><label class="fk-fld" for="fk-hips">'+l.fld_hips+' ('+um+')</label><input type="number" id="fk-hips" inputmode="decimal" '+measInputAttrs('hipsCm')+' placeholder="'+phx('104','41')+'" value="'+dispCm(state.hipsCm)+'" '+measInputHandlers('hipsCm')+'></div>'+
        '</div>'+
        '<p class="fk-hint">'+l.meas_decimal_note+'</p>'+
        (mw ? '<p class="fk-meas-warn">'+mw+'</p>' : '')+
        '<button type="button" class="fk-btn" id="fk-next"'+(screenValid('meas')?'':' disabled')+' onpointerdown="SnagFitKey.measPointer(event)" onclick="SnagFitKey.leaveMeas()">'+l.cta_continue+'</button></div>';

    } else if (s==='bra') {
      var bw=measureWarnMessage();
      R.innerHTML='<div class="fk-screen fk-screen--step"><button type="button" class="fk-back" onclick="SnagFitKey.go(\'meas\')">'+l.back+'</button>'+
        '<h1 class="fk-title fk-title--lg">'+l.bra_title+' <span class="fk-muted">'+l.bra_optional+'</span></h1>'+
        braBlock(true)+
        (bw ? '<p class="fk-meas-warn">'+bw+'</p>' : '')+
        '<button type="button" class="fk-btn" id="fk-next"'+(screenValid('bra')?'':' disabled')+' onpointerdown="SnagFitKey.submitPointer(event)" onclick="SnagFitKey.finishForm()">'+l.cta_show+'</button></div>';

    } else if (s==='card') {
      var r=state.result;
      var l2=getLang();
      var cell=function(lab,v,sub) { return '<div class="fk-sc-cell"><div class="fk-sc-l">'+lab+'</div><div class="fk-sc-v">'+(v||'–')+'</div>'+(sub?'<div class="fk-sc-sub">'+sub+'</div>':'')+'</div>'; };
      var dN=function(sz) { return state.region+' '+rnum(denimUKof(sz)); };
      var grid=cell(l2.grid_fishnets,r.fishnets)+cell(l2.grid_bras,r.bra)+cell(l2.grid_tops,r.top,r.top?letterBand(r.top):'')+cell(l2.grid_bottoms,r.bottom,r.bottom?letterBand(r.bottom):'')+cell(l2.grid_dresses,r.dress,r.dress?letterBand(r.dress):'')+cell(l2.grid_denim,r.denim,r.denim?dN(r.denim):'');
      R.innerHTML='<div class="fk-screen">'+
        '<div class="fk-cardwrap"><div class="fk-sizecard" id="fk-thecard">'+
        '<div class="fk-sc-top"><span class="fk-sc-logo">'+l2.card_lead+'</span></div>'+
        '<div class="fk-sc-name">'+state.region+l2.card_region_sfx+'</div>'+
        '<div class="fk-sc-hero"><div class="fk-sc-lab">'+l2.card_hero_lab+'</div><div class="fk-sc-val">'+r.tights+'</div></div>'+
        '<div class="fk-sc-grid">'+grid+'</div>'+
        '<div class="fk-sc-foot"><span>'+domain+'</span><span>'+l2.card_return+'</span></div>'+
        '</div></div>'+
        (r.usedMeas ? '' : '<p class="fk-sc-rec">'+l2.meas_recommend+'</p>')+
        (r.fitHelp ? '<div class="fk-sc-help">'+l2.fit_help+'</div>' : '')+
        '<div class="fk-cardactions">'+
          '<button type="button" class="fk-btn" onclick="SnagFitKey.closeToPicker()">'+l2.cta_close+'</button>'+
          (state.emailDone ? '' : '<button type="button" class="fk-btn--link" onclick="SnagFitKey.go(\'email\')">'+l2.cta_email_me+'</button>')+
          '<button type="button" class="fk-btn--link" onclick="SnagFitKey.editMeasurements()">'+l2.cta_edit_meas+'</button>'+
        '</div></div>';
    }

    // Suppress the entrance animation when we re-rendered the same screen
    // (e.g. selecting shape/height) so the panel doesn't jolt on every click.
    if (sameScreen) {
      var sc = R.querySelector('.fk-screen');
      if (sc) sc.style.animation = 'none';
    }
    // Snap to top when entering the email gate or results card so content
    // is always seen from the top after a long form scroll.
    if (!sameScreen && (s === 'email' || s === 'card')) {
      var ovsc = document.getElementById('fkoverlay');
      if (ovsc) ovsc.scrollTop = 0;
      if (R.parentElement) R.parentElement.scrollTop = 0;
    }
    if (isFitKeyOpen()) saveSessionProgress();
  }

  // ─── OVERLAY CONTROLS ─────────────────────────────────────────────────────

  function go(s) { state.screen=s; render(); }
  function setUnit(u) { state.unit=u; render(); }
  function setRegion(r) { state.region=r; var cfg=REGIONS[r]; if (cfg&&cfg.defaultUnit) state.unit=cfg.defaultUnit; render(); }
  function setMeas(f,v) {
    state[f]=toCm(v);
    if (f==='bustCm') syncBustFullFromBust();
    chk();
  }
  function setHeight(v) { state.heightCm=toCmU(v, heightUnit()); chk(); }
  // Single unified, ungendered form. Reset the per-attempt inputs and open the
  // form. path stays 'usual' so storage + PDP keep their existing shape.
  function pick() {
    Object.assign(state,{path:'usual',build:null,topSize:null,bottomSize:null,heightBand:null,heightCm:null,bustCm:null,waistCm:null,hipsCm:null,braMode:null,clothingMeasOpen:false,topChoice:null,botChoice:null});
    go('shape');
  }
  function setBuild(b) { state.build=b; render(); }
  function setH(i) { state.heightBand=i; render(); }
  function setBraMode(m) { state.braMode=m; if (m==='figure') syncBustFullFromBust(); render(); }
  function toggleClothingMeas() { state.clothingMeasOpen=!state.clothingMeasOpen; render(); }
  // Screen 5 branch: "Yes" opens the measurement path (→ meas → bra); "Skip" leaves
  // measurements/bra untouched and finishes straight away (compute() falls back to the
  // usual top/bottom sizes, exactly as leaving the old inline blocks closed did).
  function chooseAddMeas(yes) {
    if (yes) { state.clothingMeasOpen=true; go('meas'); }
    else { state.clothingMeasOpen=false; state.braMode=null; finishForm(); }
  }
  // Leaving the measurements screen: capture typed values, then advance to bra —
  // respecting the disabled state so an out-of-range value can't slip through.
  function leaveMeas() {
    syncMeasureFieldsFromDom();
    var n=document.getElementById('fk-next'); if (n&&n.disabled) return;
    go('bra');
  }
  function measPointer(e) {
    syncMeasureFieldsFromDom();
    chk();
    var n=document.getElementById('fk-next'); if (!n||n.disabled) return;
    e.preventDefault();
    go('bra');
  }
  // Back from the email gate → the last question screen the shopper actually saw.
  function emailBack() { go(state.clothingMeasOpen ? 'bra' : 'addmeas'); }
  // Mobile ghost-tap guard: the submit button fires on pointerdown (so a single
  // tap works even while a number field is focused), which means the results
  // card renders while the finger is still down. The following pointerup then
  // synthesises a click on whatever card button now sits under the finger
  // ("Edit my measurements" / "Back to shopping"), bouncing the user straight
  // back off the results. Briefly ignore card-button taps right after the card
  // appears so the size result is actually seen.
  var RESULTS_TAP_LOCK_MS = 450;
  var resultsLockUntil = 0;
  function resultsLocked() { return Date.now() < resultsLockUntil; }
  function editMeasurements() { if (resultsLocked()) return; state.screen='shape'; render(); }
  function formValid() {
    if (!state.build || state.heightBand==null) return false;
    return Boolean(state.topSize) && Boolean(state.bottomSize) && clothingMeasOptionalValid();
  }
  // Per-screen gate for the current screen's Continue button. Question screens require
  // their own answer; the optional measurement/bra screens are always passable but block
  // advancing while a filled value is out of range (the range warning shows alongside).
  function screenValid(s) {
    switch (s) {
      case 'shape':  return Boolean(state.build);
      case 'sizes':  return Boolean(state.topSize) && Boolean(state.bottomSize);
      case 'height': return state.heightBand!=null;
      case 'meas':
      case 'bra':    return clothingMeasOptionalValid();
      default:       return true;
    }
  }
  function chk() {
    var n=document.getElementById('fk-next'); if (!n) return;
    n.disabled=!screenValid(state.screen);
  }
  function finishForm() {
    syncMeasureFieldsFromDom();
    chk();
    if (!formValid()) return;
    state.topChoice=null; state.botChoice=null;
    state.result=compute();
    // Fresh completion → re-offer the email form. The results-card "email me my
    // sizes" button shows while this is false; only an actual submit sets it true,
    // so skippers (and already-identified auto-skips) still get the button.
    state.emailDone=false;
    // Fresh completion → re-arm every per-completion event guard.
    state.completeSent=false; state.completeSentEmail=false; state.signupSent=false;
    // Skip the email gate if they've already subscribed via this form.
    // skipEmail() intentionally does NOT set this flag so skippers are asked again.
    var emailAlreadyDone = false;
    try { emailAlreadyDone = !!localStorage.getItem('snag_fk_email_done'); } catch(e){}
    // If they're already identified in Klaviyo (cookie set by a previous email click or form),
    // treat them as done — skip the gate and set the flag so future visits also skip.
    if (!emailAlreadyDone) {
      try {
        var hasKlaCookie = document.cookie.split(';').some(function(c){ return c.trim().indexOf('__kla_id=') === 0; });
        if (hasKlaCookie) { emailAlreadyDone = true; localStorage.setItem('snag_fk_email_done', '1'); }
      } catch(e){}
    }
    // Snapshot "did we already know this person" BEFORE any identify() of ours runs.
    // Reported as `Known Visitor` on every fit-key event: false = new to us.
    state.knownVisitor = emailAlreadyDone;
    if (emailAlreadyDone) {
      // Silently push updated sizes to their Klaviyo profile without re-prompting.
      // No email needed — Klaviyo links this call to the existing profile via its own __kla_id cookie.
      var r2 = state.result || {};
      var kl2 = global.klaviyo || null;
      function klPush2(args) {
        if (kl2 && typeof kl2.push === 'function') kl2.push(args);
        else { var q2 = global._learnq = global._learnq || []; q2.push(args); }
      }
      var props2 = { 'Snag Region': r2.region || state.region };
      Object.assign(props2, fkSizeProps(r2, 'Snag '));
      props2['Snag Fit Key Completed'] = new Date().toISOString();
      try { klPush2(['identify', props2]); } catch(ex){}
      // Auto-skip path: no email in hand, but this visitor IS already identified, so
      // the cookie-keyed onsite route attaches correctly. The event fires once, here.
      trackFitKeyComplete('known_visitor');
    }
    go(emailAlreadyDone ? 'card' : 'email');
    resultsLockUntil = Date.now() + RESULTS_TAP_LOCK_MS;
    onFitKeyComplete(state.result);
  }
  function submitPointer(e) {
    syncMeasureFieldsFromDom();
    chk();
    var n=document.getElementById('fk-next'); if (!n||n.disabled) return;
    // First tap while a number field is focused often only blurs it (mobile keyboard
    // dismiss) — pointerdown fires before blur so we can submit on one tap.
    e.preventDefault();
    finishForm();
  }
  function reset() {
    clearSessionProgress();
    lastScreen=null;
    Object.assign(state,{screen:'intro',path:null,build:null,clothingMeasOpen:false,topSize:null,bottomSize:null,heightBand:null,heightCm:null,bustCm:null,waistCm:null,hipsCm:null,braMode:null,braBand:null,braCup:null,underbustCm:null,bustFullCm:null,topChoice:null,botChoice:null,result:null});
    render();
  }
  var lastFocus=null;
  function onKeydown(e) { if ((e.key==='Escape'||e.key==='Esc')&&isFitKeyOpen()) close(); }

  function open() {
    var r=document.getElementById('snag-fit-key-root');
    if (r) r.removeAttribute('hidden');
    var o=document.getElementById('fkoverlay'); if (!o) return;
    lastFocus=document.activeElement;
    o.classList.add('on'); o.setAttribute('aria-hidden','false');
    document.body.style.overflow='hidden';
    lastScreen=null;
    // Resume only within the same browser session (sessionStorage). Completed
    // sizes stay in localStorage for PDP auto-select, but a fresh visit always
    // starts the quiz from the intro — not hours-old form progress.
    if (!restoreFromSession()) reset();
    else render();
    document.addEventListener('keydown',onKeydown);
    var x=o.querySelector('.fkbar .x'); if (x) { try { x.focus({preventScroll:true}); } catch(e){ x.focus(); } }
  }
  function close() {
    saveSessionProgress();
    var o=document.getElementById('fkoverlay'); if (!o) return;
    o.classList.remove('on'); o.setAttribute('aria-hidden','true');
    document.body.style.overflow='';
    document.removeEventListener('keydown',onKeydown);
    var r=document.getElementById('snag-fit-key-root');
    if (r && r.dataset.displayMode==='hidden_overlay') r.setAttribute('hidden','');
    if (lastFocus && typeof lastFocus.focus==='function') { try { lastFocus.focus({preventScroll:true}); } catch(e){} lastFocus=null; }
  }

  // Close the overlay and, on a product page, scroll back to the size variant picker.
  function closeToPicker() {
    if (resultsLocked()) return;
    close();
    var el = document.querySelector('.product-block__variant_picker');
    if (el && typeof el.scrollIntoView === 'function') {
      setTimeout(function () { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 80);
    }
  }
  function applyConfig(cfg) {
    cfg=cfg||global.snagFitKeyConfig||{};
    var domain=cfg.domain||(global.location&&global.location.hostname)||'';
    var region=cfg.region||regionFromHost(domain);
    var reg=REGIONS[region]||{};
    state.region=region;
    state.unit=cfg.defaultUnit||reg.defaultUnit||'cm';
    state.domain=domain;
  }

  // ─── STORAGE & EVENTS ─────────────────────────────────────────────────────

  var STORAGE_KEY      = 'snag_fit_v1';
  var SESSION_KEY      = 'snag_fit_session_v1';
  var LEGACY_KEY       = 'snag_size_result';
  var STORAGE_DATE_KEY = 'snag_size_calc_date';
  var SEEN_COOKIE      = 'snag_size_calc_seen';
  var SEEN_DAYS        = 90;

  function readStored() { try { var raw=localStorage.getItem(STORAGE_KEY); return raw?JSON.parse(raw):null; } catch(e){ return null; } }

  function clearSessionProgress() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  function saveSessionProgress() {
    if (state.screen === 'intro') return;
    var payload = {
      screen: state.screen,
      inputs: snapshotInputs(),
      region: state.region,
      unit: state.unit,
      result: state.result,
      t: Date.now(),
    };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload)); } catch (e) {}
  }

  function restoreFromSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return false;
      var stored = JSON.parse(raw);
      if (!stored || !stored.screen || stored.screen === 'intro') return false;
      // Ignore progress saved against a screen that no longer exists (e.g. the
      // removed gender/path screens) — otherwise render() finds no branch and the
      // panel comes up blank. Falling through makes open() reset to the intro.
      if (STEPS.indexOf(stored.screen) === -1) return false;
      var inputs = stored.inputs;
      if (inputs) {
        for (var k in inputs) {
          if (inputs.hasOwnProperty(k)) state[k] = inputs[k];
        }
      }
      if (stored.region) state.region = stored.region;
      if (stored.unit) state.unit = stored.unit;
      // Drop any restored measurement that is out of range, so a stale/garbage value
      // (e.g. a cm number typed while in inches mode) never comes back from a prior
      // session to surface a warning the user can't explain.
      ['bustCm','waistCm','hipsCm','underbustCm','bustFullCm'].forEach(function (f) {
        if (state[f] != null && state[f] !== '' && !measureCmInRange(f, state[f])) state[f] = null;
      });
      state.screen = stored.screen;
      state.result = stored.result || null;
      return true;
    } catch (e) {
      return false;
    }
  }

  function writeStored(result) {
    if (!result) return;
    clearSessionProgress();
    var payload={ r:result, region:result.region||state.region||'UK', unit:result.unit||state.unit||'cm', inputs:snapshotInputs(), t:Date.now() };
    try { localStorage.setItem(STORAGE_KEY,JSON.stringify(payload)); } catch(e){}
    try {
      var exp=new Date(Date.now()+SEEN_DAYS*864e5).toUTCString();
      document.cookie=SEEN_COOKIE+'=1; expires='+exp+'; path=/';
    } catch(e2){}
    var resolved=getSizeForProduct(payload,global.snagSizePdpConfig||{})||result.tights;
    if (resolved) {
      try { localStorage.setItem(LEGACY_KEY,resolved); localStorage.setItem(STORAGE_DATE_KEY,String(Date.now())); } catch(e){}
    }
  }

  function onFitKeyComplete(result) {
    writeStored(result);
    var resolved=getSizeForProduct({r:result},global.snagSizePdpConfig||{})||result.tights;
    if (resolved) {
      selectSizeVariant(resolved);
      updateTrigger(resolved);
    }
    try { global.dispatchEvent(new CustomEvent('snag:fitkey:complete',{detail:result})); } catch(e){}
  }

  // ─── PDP INTEGRATION ──────────────────────────────────────────────────────

  var AUTO_TRIGGER_TAGS    = ['sg-tights','pt-chubrubs','b-tights'];
  var AUTO_TRIGGER_SECONDS = 15;

  function setSnagCookie(name,value,days) {
    document.cookie=name+'='+encodeURIComponent(value)+'; expires='+new Date(Date.now()+days*864e5).toUTCString()+'; path=/';
  }
  function getSnagCookie(name) {
    var m=document.cookie.match(new RegExp('(?:^|; )'+name.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')+'=([^;]*)'));
    return m?decodeURIComponent(m[1]):null;
  }

  function tagMatches(tags,patterns) {
    var list=(Array.isArray(tags)?tags:[]).map(function(t){return String(t).toLowerCase();});
    return patterns.some(function(p){return list.indexOf(String(p).toLowerCase())!==-1;});
  }

  function getSizeForProduct(result,meta) {
    if (!result) return null;
    var r=result.r||result;
    var type=String((meta&&meta.productType)||'').toLowerCase();
    var tags=(meta&&meta.productTags)||[];
    if (tagMatches(tags,AUTO_TRIGGER_TAGS)||/tight|chub|short/i.test(type)) return r.tights||null;
    if (tagMatches(tags,['sg-jeans'])||/denim|jean/i.test(type))            return r.denim||null;
    if (tagMatches(tags,['pt-bra'])||/bra/i.test(type))                     return (r.bra&&r.bra!=='-')?r.bra:null;
    if (/fishnet/i.test(type)||tagMatches(tags,['sg-fishnets']))             return r.fishnets||r.tights||null;
    if (/dress/i.test(type)||tagMatches(tags,['pt-dress']))                  return r.dress||r.top||null;
    if (/skirt/i.test(type)||tagMatches(tags,['pt-skirt']))                  return r.bottom||r.dress||r.top||null;
    if (/top|shirt|blouse|jumper|hoodie|cardigan|bodysuit/i.test(type))      return r.top||null;
    if (/bottom|trouser|legging|jogger|ottoman/i.test(type))                 return r.bottom||null;
    return r.bottom||r.top||r.tights||null;
  }

  function getStoredSizeForProduct(meta) {
    var fk=readStored();
    if (fk&&fk.r) { var resolved=getSizeForProduct(fk,meta); if (resolved) return resolved; }
    return localStorage.getItem(LEGACY_KEY);
  }

  function normalizeSize(v) { return String(v||'').trim().toLowerCase().replace(/\s+/g,' '); }

  function getProductMain()    { return document.querySelector('.product-main'); }
  function getVariantSelects() { var pm=getProductMain(); return pm?pm.querySelector('variant-selects[id^="variant-selects-"]'):null; }
  // Identifies the variant option that carries the SIZE, across localised storefronts.
  // The picker emits name="options[{{ option.name }}]" straight from the product data, so
  // a German product reads options[Größe] — which the old /size/i never matched. The
  // result on DE: getSizeInputs() returned an empty set, auto-select silently did nothing,
  // and isSizeOptionSelected() was permanently false, while the trigger label still read
  // "Deine letzte Größe war C" because updateTrigger() never touches the picker at all.
  // Listed localisations are matched here; anything unlisted is caught by the permissive
  // fallback below, so a new market cannot silently lose auto-select again.
  var SIZE_OPTION_RE = /size|gr(ö|oe|o)(ß|ss)e|taille|talla|taglia|maat|storlek|rozmiar/i;

  // `anyOption` is passed ONLY by callers that go on to match against the option VALUE.
  // That is what makes the fallback safe: a colour option is never selected unless one of
  // its values literally equals the recommended size. isSizeOptionSelected deliberately
  // does not pass it — it must not mistake a chosen colour for a chosen size.
  function getSizeInputs(anyOption) {
    var vs=getVariantSelects(); if (!vs) return [];
    var all=Array.from(vs.querySelectorAll('.product-form__input input[type="radio"]'));
    var named=all.filter(function(i){return SIZE_OPTION_RE.test(i.name||'');});
    return (named.length || !anyOption) ? named : all;
  }
  function getSizeSelects(anyOption) {
    var vs=getVariantSelects(); if (!vs) return [];
    var all=Array.from(vs.querySelectorAll('.product-form__input--dropdown select.field__input'));
    var named=all.filter(function(s){return SIZE_OPTION_RE.test(s.name||'');});
    return (named.length || !anyOption) ? named : all;
  }

  function isSizeOptionSelected() {
    var checked=getSizeInputs().find(function(i){return i.checked;});
    if (checked) {
      var leg=checked.closest('.product-form__input');
      var legText=leg&&leg.querySelector('[data-variant-legend]');
      if (legText&&legText.textContent.trim().toLowerCase().includes('select your size')) return false;
      return Boolean(checked.value);
    }
    var sel=getSizeSelects()[0]; return sel?Boolean(sel.value):false;
  }

  function findMatchingSizeInput(size) {
    var target=normalizeSize(size); if (!target) return null;
    var vs=getVariantSelects();
    // Prefer a confirmed size option; only widen to every option if none was identified.
    var strict=getSizeInputs();
    var inputs=strict.length ? strict : getSizeInputs(true);
    var match=inputs.find(function(i){return normalizeSize(i.value)===target;});
    if (match) return match;
    match=inputs.find(function(i){
      var label=vs&&vs.querySelector('label[for="'+CSS.escape(i.id)+'"]');
      return label&&normalizeSize(label.textContent)===target;
    });
    if (match) return match;
    // Fuzzy substring matching runs ONLY on a confirmed size option. On the widened set it
    // would happily select a colour whose label merely contains the recommended letter.
    if (!strict.length) return null;
    return inputs.find(function(i){
      var label=vs&&vs.querySelector('label[for="'+CSS.escape(i.id)+'"]');
      var lt=normalizeSize(label&&label.textContent);
      return lt.includes(target)||target.includes(lt);
    })||null;
  }

  function trySelectSize(size) {
    var input=findMatchingSizeInput(size);
    if (input) { input.click(); input.dispatchEvent(new Event('change',{bubbles:true})); return true; }
    // Widened set is safe here: the select is only chosen if one of its OPTION VALUES
    // equals the recommended size.
    var sel=getSizeSelects(true).find(function(s){return Array.from(s.options).some(function(o){return normalizeSize(o.value)===normalizeSize(size);});});
    if (sel) {
      var opt=Array.from(sel.options).find(function(o){return normalizeSize(o.value)===normalizeSize(size);});
      if (opt) { sel.value=opt.value; sel.dispatchEvent(new Event('change',{bubbles:true})); return true; }
    }
    return false;
  }

  function selectSizeVariant(size) {
    if (trySelectSize(size)) return true;
    // Explicit fallback: a "Short <letter>" recommendation (e.g. "Short F") on a
    // product that has no Short variant falls back to the plain letter ("F").
    var m=/^short\s+(.+)$/i.exec(String(size||'').trim());
    if (m && trySelectSize(m[1])) return true;
    return false;
  }

  function isFitKeyOpen() { var o=document.getElementById('fkoverlay'); return Boolean(o&&o.classList.contains('on')); }

  function updateDenimNote(show) {
    var note=document.getElementById('snag-calc-denim-note');
    if (note) note.hidden=!show;
  }

  function updateTrigger(size) {
    var trigger=document.getElementById('snag-calc-trigger');
    if (!trigger) return;
    var defLabel=trigger.querySelector('.snag-calc-trigger__label--default');
    var retLabel=trigger.querySelector('.snag-calc-trigger__label--return');
    var sizeEl=trigger.querySelector('.snag-calc-trigger__size');
    var de=state.region==='DE';
    var cfg=global.snagSizePdpConfig||{};
    if (size) {
      if (sizeEl) sizeEl.textContent=size;
      if (defLabel) defLabel.hidden=true;
      if (retLabel) retLabel.hidden=false;
      trigger.classList.add('has-stored-size');
      trigger.setAttribute('aria-label', de
        ? 'Deine letzte ermittelte Größe war '+size+'. Größenfinder öffnen, um sie neu zu ermitteln.'
        : 'You previously measured size '+size+'. Open the size finder to recalculate.');
      updateDenimNote(Boolean(cfg.denimFittedStyle));
    } else {
      if (defLabel) defLabel.hidden=false;
      if (retLabel) retLabel.hidden=true;
      trigger.classList.remove('has-stored-size');
      trigger.setAttribute('aria-label', de ? 'Finde deine Snag-Größe' : 'Find your Snag size');
      updateDenimNote(false);
    }
  }

  function wireTrigger() {
    var trigger=document.getElementById('snag-calc-trigger');
    if (!trigger || trigger._snagWired) return;
    trigger._snagWired=true;
    trigger.addEventListener('click',function(){
      if (document.getElementById('fkoverlay')&&typeof global.openFitKey==='function') { global.openFitKey(); return; }
      if (typeof global.openSnagCalcModal==='function') global.openSnagCalcModal();
    });
    var stored=getStoredSizeForProduct(global.snagSizePdpConfig||{});
    if (stored) updateTrigger(stored);
  }

  function initPdp(config) {
    if (!config||!config.isProductPage||global.__snagSizePdpInitialized) return;
    global.__snagSizePdpInitialized=true;

    var stored=getStoredSizeForProduct(config);
    if (stored) {
      var apply=function(){selectSizeVariant(stored);};
      apply(); setTimeout(apply,500); setTimeout(apply,1500);
      updateTrigger(stored);
    }

    // ─── TIMED AUTO-POPUP: OFF ──────────────────────────────────────────────
    // Parked on the drafts deliberately. Switching the popup back on is its own
    // release decision, separate from the email signup gate and separate again from
    // this multi-screen UX change. Keeping it off here means neither release can
    // silently re-enable the popup on the 5 stores that parked it on live on
    // 2026-07-30.
    // TO RE-ENABLE: flip this to false. Nothing else in this file needs changing.
    // Everything above still runs — stored-size auto-select on the variant picker
    // and the "Find your Snag size" trigger button (manual opens ignore the cookie).
    var AUTO_POPUP_DISABLED = true;
    if (AUTO_POPUP_DISABLED) return;

    // Auto-open runs on all viewports (desktop + mobile).
    if (getSnagCookie(SEEN_COOKIE)) return;

    var activeSeconds=0, sizeSelected=isSizeOptionSelected(), addToCartClicked=false, timerId=null;
    function clearTimer() { if (timerId!==null){clearInterval(timerId);timerId=null;} }
    function tryTrigger() {
      if (isSizeOptionSelected()){sizeSelected=true;clearTimer();return;}
      if (activeSeconds>=AUTO_TRIGGER_SECONDS&&!sizeSelected&&!addToCartClicked&&!getSnagCookie(SEEN_COOKIE)&&!isFitKeyOpen()) {
        open(); clearTimer();
      }
    }
    function tick(){if(document.visibilityState!=='hidden')activeSeconds++;tryTrigger();}
    document.addEventListener('visibilitychange',function(){
      if(document.visibilityState==='hidden')clearTimer(); else if(!timerId) timerId=setInterval(tick,1000);
    });
    timerId=setInterval(tick,1000);

    var pm=getProductMain();
    if (pm) {
      pm.addEventListener('change',function(e){
        if(e.target instanceof HTMLInputElement&&e.target.type==='radio'&&/size/i.test(e.target.name||'')){sizeSelected=true;clearTimer();}
        if(e.target instanceof HTMLSelectElement&&/size/i.test(e.target.name||'')){sizeSelected=true;clearTimer();}
      });
      pm.addEventListener('click',function(e){
        if(e.target.closest('form[data-type="add-to-cart-form"] button.product-form__submit[name="add"]')){addToCartClicked=true;clearTimer();}
      });
    }
  }

  function hasAutoTriggerTag(tags) {
    return (Array.isArray(tags)?tags:[]).some(function(t){return AUTO_TRIGGER_TAGS.indexOf(String(t).toLowerCase())!==-1;});
  }

  // ─── EMAIL SIGNUP GATE ────────────────────────────────────────────────────

  // Skipper: no email, and possibly not identified at all — the onsite route is the
  // only one available, so it is best-effort. The event fires once, here.
  function skipEmail() { trackFitKeyComplete('skipped'); go('card'); }

  function submitEmailSignup(e) {
    e.preventDefault();
    var emailEl = document.getElementById('fk-femail');
    var fnameEl = document.getElementById('fk-fname');
    var errEl   = document.getElementById('fk-email-error');
    var email   = emailEl ? emailEl.value.trim() : '';
    var fname   = fnameEl ? fnameEl.value.trim() : '';

    // Basic client-side validation (type="email" + novalidate means we check manually)
    if (!email || email.indexOf('@') <= 0 || email.lastIndexOf('.') <= email.indexOf('@')) {
      if (errEl) { errEl.hidden = false; errEl.textContent = 'Please enter a valid email address.'; }
      return;
    }
    if (errEl) errEl.hidden = true;

    var r   = state.result || {};
    var kv  = ((global.snagFitKeyConfig || {}).klaviyo) || {};
    var kl  = global.klaviyo || null;
    function klPush(args) {
      if (kl && typeof kl.push === 'function') kl.push(args);
      else { var q = global._learnq = global._learnq || []; q.push(args); }
    }

    // Step 1 — identify profile + set size properties.
    // Klaviyo is schema-less: unknown properties are auto-created on first send.
    // Falsy values are omitted so a partial run never blanks an existing property.
    var props = { '$email': email, 'Snag Region': r.region || state.region };
    // Only send first name when supplied — an empty field must not blank an existing $first_name.
    if (fname) props['$first_name'] = fname;
    // Size + local-size properties (skips empty / "-" so a partial retake never blanks one).
    Object.assign(props, fkSizeProps(r, 'Snag '));
    props['Snag Fit Key Completed'] = new Date().toISOString();
    // Reporting: a durable "this profile submitted the fit key form" flag plus the
    // source, set ONLY on this path (skip and auto-skip never write them), so a
    // segment on `Snag Fit Key Email Captured is set` is exactly your signup list.
    // This is the MOST RECENT capture. For first-ever capture use the first occurrence
    // of the Fit Key Email Signup metric in Klaviyo — more reliable than trying to make
    // a property write-once from the browser, and it survives cleared localStorage.
    props['Snag Fit Key Email Captured'] = new Date().toISOString();
    props['Snag Fit Key Signup Source']  = FK_SIGNUP_SOURCE;
    klPush(['identify', props]);

    // Step 2 — subscribe to list via client endpoint (public key only, safe in browser).
    // Omitting the subscriptions object causes Klaviyo to default to email marketing
    // consent — simpler and avoids the 400 caused by nested subscriptions format.
    //
    // ORDER IS LOAD-BEARING. The Klaviyo flow is triggered by "Completed Fit Key" and
    // gated on list membership, so the profile MUST be subscribed before that event
    // lands or it never enters the flow. Step 2 and Step 3 used to be two independent
    // fire-and-forget fetches issued back to back — the event usually won the race
    // (smaller payload, no list relationship to resolve). Step 3 is now CHAINED onto
    // Step 2's response.
    //
    // client/subscriptions answers 202 (queued, not yet committed), so the ack alone
    // does not prove membership — we wait SUBSCRIBE_COMMIT_MS after it before firing.
    // The shopper never waits on any of this: the go('card') at the end of this
    // function runs synchronously, before any of these callbacks, so they are already
    // reading their sizes while the subscribe settles.
    var SUBSCRIBE_COMMIT_MS = 2500;
    var submitEventsTimer = null;
    function fireSubmitEvents() {
      if (submitEventsTimer) { clearTimeout(submitEventsTimer); submitEventsTimer = null; }
      try { global.removeEventListener('pagehide', fireSubmitEvents); } catch(e){}
      // Step 3 — both submit-path metrics, keyed by EMAIL so they land on the profile
      // we just identified even when it is brand new. Each is guarded to fire once, so
      // being called twice (timer AND pagehide) is harmless.
      trackFitKeyCompleteByEmail(email);   // all completions, Path='email_submit'
      trackFitKeyEmailSignup(email);       // signups only — the reportable number
    }
    function afterSubscribeAttempt() {
      submitEventsTimer = setTimeout(fireSubmitEvents, SUBSCRIBE_COMMIT_MS);
    }
    // If they leave before the timer runs, fire immediately rather than lose the events —
    // a page being torn down stops all timers, so the 2500ms wait would simply never
    // elapse. keepalive on the fetches lets those requests still complete. Registered now,
    // not in afterSubscribeAttempt, so it also covers leaving before the subscribe settles.
    // pagehide only (not visibilitychange) so a tab switch does not fire them early.
    try { global.addEventListener('pagehide', fireSubmitEvents); } catch(e){}

    if (kv.publicKey && kv.listId) {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(kv.publicKey), {
        method: 'POST',
        // keepalive: the subscribe fires the instant they submit, so closing the tab
        // within its round trip used to abort it and they were never subscribed at all.
        keepalive: true,
        headers: { 'content-type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              // custom_source lands in the consent record's custom_method_detail, so
              // every fit-key signup is attributable at the consent level — separable
              // from footer / popup / checkout signups on the same list, and durable
              // even if the events never fire. This is the number to report on.
              custom_source: FK_SIGNUP_SOURCE,
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: kv.listId } } }
          }
        })
      // Same handler for success and failure: a failed subscribe must still not lose
      // the completion event. Never fires before the subscribe request has settled.
      }).then(afterSubscribeAttempt, afterSubscribeAttempt);
    } else {
      // Nothing to subscribe to, so there is no ordering constraint to respect.
      fireCompletionEvent();
    }

    // Only store the completion flag — never persist PII like email in localStorage.
    // On retakes, Klaviyo links identify() calls to the same profile via its own __kla_id cookie.
    try { localStorage.setItem('snag_fk_email_done', '1'); } catch(e){}
    state.emailDone = true;   // hide the "email me my sizes" button on the results card
    go('card');
  }

  // ─── EXPORTS ──────────────────────────────────────────────────────────────

  global.SnagFitKey = { state:state, compute:compute, go:go, open:open, close:close, closeToPicker:closeToPicker, reset:reset, render:render, setUnit:setUnit, setRegion:setRegion, setMeas:setMeas, setHeight:setHeight, pick:pick, setBuild:setBuild, setH:setH, setBraMode:setBraMode, toggleClothingMeas:toggleClothingMeas, chooseAddMeas:chooseAddMeas, leaveMeas:leaveMeas, measPointer:measPointer, emailBack:emailBack, finishForm:finishForm, submitPointer:submitPointer, editMeasurements:editMeasurements, chk:chk, applyConfig:applyConfig, formValid:formValid, screenValid:screenValid, showMeasureRangeWarning:showMeasureRangeWarning, skipEmail:skipEmail, submitEmailSignup:submitEmailSignup };
  global.openFitKey  = open;
  global.closeFitKey = close;

  global.SnagSizePDP = {
    setSnagCookie:setSnagCookie, getSnagCookie:getSnagCookie,
    getSizeForProduct:getSizeForProduct, getStoredSizeForProduct:getStoredSizeForProduct,
    selectSizeVariant:selectSizeVariant, isFitKeyOpen:isFitKeyOpen,
    initPdp:initPdp, updateTrigger:updateTrigger,
    saveSnagSizeResult:function(size){
      if(!size) return;
      try{localStorage.setItem(LEGACY_KEY,size);localStorage.setItem(STORAGE_DATE_KEY,String(Date.now()));}catch(e){}
      setSnagCookie(SEEN_COOKIE,'1',90);
      updateTrigger(size);
    },
  };

  global.SnagSize = {
    get:readStored,
    set:function(r){writeStored(r);},
    clear:function(){try{localStorage.removeItem(STORAGE_KEY);}catch(e){}},
  };

  global.updateSnagCalcTrigger = updateTrigger;

  // ─── LEGACY CALCULATOR BRIDGE ─────────────────────────────────────────────
  // Old per-type calculators were replaced by Fit Key. Legacy templates still
  // link to #snag-size-calculator etc.; route those to Fit Key when enabled,
  // hide them while FIT_KEY_DISABLED is true.

  var LEGACY_CALC_HASH_RE = /#(?:snag-size-calculator|snag-bra-calculator|snag-skirt-calculator|bra-size-calculator)$/;

  var FIT_KEY_KILLSWITCH_CSS = [
    '.snag-calc-trigger',
    '.snag-fk-banner',
    '#snag-fit-key-root',
    '#size-calculator-button',
    'a[href="#snag-size-calculator"]',
    'a[href="#snag-bra-calculator"]',
    'a[href="#snag-skirt-calculator"]',
    'a[href="#bra-size-calculator"]',
    'a[href*="#snag-size-calculator"]',
    'a[href*="#snag-bra-calculator"]',
    'a[href*="#snag-skirt-calculator"]',
    'a[href*="#bra-size-calculator"]'
  ].join(',') + '{display:none !important;}';

  function wireLegacyCalculatorLinks() {
    if (global.__snagLegacyCalcWired) return;
    global.__snagLegacyCalcWired = true;
    document.addEventListener('click', function (e) {
      var el = e.target.closest('#size-calculator-button, a[href]');
      if (!el) return;
      if (el.id === 'size-calculator-button') {
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
        return;
      }
      var href = el.getAttribute('href') || '';
      if (!LEGACY_CALC_HASH_RE.test(href)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
    }, true);
  }

  // ─── DUPLICATE-RENDER CLEANUP (defensive — no page currently trips it) ─────
  // snag-fit-key.liquid is rendered from four places: product-template, the
  // custom-size-calculator and custom-find-snag-size sections, and the standalone
  // snag-fit-key section. Nothing today renders it twice on one page: both custom
  // sections self-suppress with `unless template.name == 'product'` (so they go quiet
  // on product pages, bras/denim suffixes included, where product-template already
  // renders it), and no non-product template carries more than one of them. This
  // function exists so that adding a second one in the theme editor degrades loudly
  // instead of silently.
  //
  // If it ever did happen, the damage would be a duplicated DOM, not duplicated
  // Klaviyo events — the module is single-instance via the `if (global.SnagFitKey)
  // return;` guard at the top of this file, so a second <script> copy bails before
  // defining anything. The DOM damage is what bites: every getElementById below
  // resolves to whichever copy parsed first, so a shopper typing into a second
  // copy's email field would have submitEmailSignup read the FIRST (empty) input
  // and reject a valid address.
  //
  // So: keep one root, drop the rest. A copy that is not a hidden overlay wins, since
  // a standalone/auto-open render owns the screen; otherwise keep the first.
  function dedupeOverlays() {
    var roots = document.querySelectorAll('#snag-fit-key-root');
    if (roots.length < 2) return;
    var keep = roots[0], i;
    for (i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-display-mode') !== 'hidden_overlay') { keep = roots[i]; break; }
    }
    for (i = roots.length - 1; i >= 0; i--) {
      if (roots[i] !== keep && roots[i].parentNode) roots[i].parentNode.removeChild(roots[i]);
    }
    try {
      if (global.console && console.warn) {
        console.warn('[snag-fit-key] removed ' + (roots.length - 1) + ' duplicate overlay(s) — the snippet is rendered more than once on this page; check the template sections.');
      }
    } catch(e){}
  }

  // ─── BOOT ─────────────────────────────────────────────────────────────────

  function boot() {
    if (FIT_KEY_DISABLED) {
      // Hide every entry point (trigger, banner, overlay, legacy calc UI) and bail.
      try {
        var st=document.createElement('style');
        st.id='snag-fit-key-killswitch';
        st.textContent=FIT_KEY_KILLSWITCH_CSS;
        (document.head||document.documentElement).appendChild(st);
      } catch(e){}
      return;
    }
    dedupeOverlays();
    applyConfig(global.snagFitKeyConfig);
    wireTrigger();
    wireLegacyCalculatorLinks();
    if (global.snagSizePdpConfig) initPdp(global.snagSizePdpConfig);
  }

  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',boot);
  else boot();

})(typeof window !== 'undefined' ? window : this);
